'use client';

import React from 'react';
import styled, { css } from 'styled-components';

export type CardElevation = 'flat' | 'raised' | 'drawer' | 'floating';

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
  /** Surface elevation. Defaults to `flat` (no shadow, hairline outline only). */
  elevation?: CardElevation;
  /** HTML element to render. Defaults to `section`. */
  as?: keyof JSX.IntrinsicElements;
  /** Inset padding. Defaults to `lg` (16px). */
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  children?: React.ReactNode;
}

const PADDING_MAP: Record<NonNullable<CardProps['padding']>, string | undefined> = {
  none: '0',
  sm: undefined,
  md: undefined,
  lg: undefined,
  xl: undefined,
};

const Surface = styled.section<{
  $elevation: CardElevation;
  $padding: NonNullable<CardProps['padding']>;
}>`
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.card};
  ${(p) =>
    p.$elevation !== 'flat' &&
    css`
      box-shadow: ${p.theme.elevation[p.$elevation].shadow};
    `}
  padding: ${(p) => {
    const explicit = PADDING_MAP[p.$padding];
    if (explicit !== undefined) return explicit;
    return {
      sm: p.theme.space[2],
      md: p.theme.space[3],
      lg: p.theme.space[4],
      xl: p.theme.space[6],
    }[p.$padding as 'sm' | 'md' | 'lg' | 'xl'];
  }};
`;

/**
 * Generic surface primitive: 1px outline, card-radius corner, optional
 * elevation. Use as the default container for grouped content (KPI tile,
 * stop-by-stop list, drawer body). For sectioned headers and footers use
 * {@link Panel} instead.
 */
export function Card({ elevation = 'flat', as, padding = 'lg', children, ...rest }: CardProps) {
  return (
    <Surface as={as as never} $elevation={elevation} $padding={padding} {...rest}>
      {children}
    </Surface>
  );
}
